class CustomReviewCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  track = null;
  /** @type {Element[]} */
  dots = [];
  /** @type {boolean} */
  autoplay = false;
  /** @type {number} */
  autoplaySpeed = 4000;
  /** @type {AbortController | null} */
  abortController = null;
  /** @type {number | undefined} */
  autoplayTimer = undefined;

  connectedCallback() {
    this.track = this.querySelector('[data-review-track]');
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = Number(this.dataset.autoplaySpeed) || 4000;

    if (!this.track) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.track.addEventListener('scroll', this.handleScroll, { signal, passive: true });
    this.addEventListener('focusin', this.stopAutoplay, { signal });
    this.addEventListener('focusout', this.startAutoplay, { signal });

    this.resizeObserver = new ResizeObserver(() => {
      this.setupPagination();
    });
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.abortController?.abort();
    this.stopAutoplay();
  }

  setupPagination = () => {
    const container = this.querySelector('[data-review-dots-container]');
    if (!(container instanceof HTMLElement) || !this.track) return;

    const cards = Array.from(this.track.children);
    if (!cards.length) return;

    const visibleCards = window.innerWidth >= 990 ? 3 : 1;
    const pageCount = Math.max(1, cards.length - visibleCards + 1);

    container.innerHTML = '';
    this.dots = [];

    if (pageCount <= 1) {
      container.style.display = 'none';
      this.stopAutoplay();
      return;
    }

    container.style.display = 'flex';

    // Left Arrow
    const prevArrow = document.createElement('button');
    prevArrow.className = 'custom-review-carousel__arrow custom-review-carousel__arrow--prev';
    prevArrow.type = 'button';
    prevArrow.setAttribute('aria-label', 'Previous reviews');
    prevArrow.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>`;
    prevArrow.addEventListener('click', () => {
      this.stopAutoplay();
      this.goToPrevPage();
      this.startAutoplay();
    });
    container.appendChild(prevArrow);

    // Dots wrapper
    const dotsWrapper = document.createElement('div');
    dotsWrapper.className = 'custom-review-carousel__dots-wrapper';

    for (let i = 0; i < pageCount; i++) {
      const button = document.createElement('button');
      button.className = 'custom-review-carousel__dot';
      button.type = 'button';
      button.dataset.reviewDot = i.toString();
      button.setAttribute('aria-label', `Show review page ${i + 1}`);

      const { signal } = this.abortController || {};
      button.addEventListener('click', this.handleDotClick, { signal });

      dotsWrapper.appendChild(button);
      this.dots.push(button);
    }
    container.appendChild(dotsWrapper);

    // Right Arrow
    const nextArrow = document.createElement('button');
    nextArrow.className = 'custom-review-carousel__arrow custom-review-carousel__arrow--next';
    nextArrow.type = 'button';
    nextArrow.setAttribute('aria-label', 'Next reviews');
    nextArrow.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>`;
    nextArrow.addEventListener('click', () => {
      this.stopAutoplay();
      this.goToNextPage();
      this.startAutoplay();
    });
    container.appendChild(nextArrow);

    this.updatePagination();
    this.startAutoplay();
  };

  handleScroll = () => {
    window.requestAnimationFrame(() => this.updatePagination());
  };

  /**
   * @param {MouseEvent} event
   */
  handleDotClick = (event) => {
    if (!(event.currentTarget instanceof HTMLElement)) return;
    const index = Number(event.currentTarget.dataset.reviewDot) || 0;
    this.stopAutoplay();
    this.scrollToPage(index);
    this.startAutoplay();
  };

  /**
   * @param {PointerEvent} event
   */
  handlePointerDown = (event) => {
    const track = this.track;
    if (!track) return;
    if (event.button !== 0 || !(event.target instanceof Element) || event.target.closest('button, a, input, textarea, select')) return;
    if (track.scrollWidth <= track.clientWidth) return;

    this.stopAutoplay();
    this.setAttribute('dragging', '');

    const startX = event.clientX;
    const startScrollLeft = track.scrollLeft;
    let hasMoved = false;

    /**
     * @param {PointerEvent} moveEvent
     */
    const handlePointerMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      if (Math.abs(delta) > 3) hasMoved = true;
      track.scrollLeft = startScrollLeft - delta;
    };

    const handlePointerUp = () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
      this.removeAttribute('dragging');
      if (hasMoved) this.snapToNearestPage();
      this.startAutoplay();
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
  };

  startAutoplay = () => {
    if (!this.autoplay || this.dots.length === 0 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stopAutoplay();
    this.autoplayTimer = window.setInterval(() => this.goToNextPage(), this.autoplaySpeed);
  };

  stopAutoplay = () => {
    window.clearInterval(this.autoplayTimer);
  };

  goToNextPage() {
    const currentPage = this.getCurrentPage();
    const nextPage = currentPage >= this.dots.length - 1 ? 0 : currentPage + 1;
    this.scrollToPage(nextPage);
  }

  goToPrevPage() {
    const currentPage = this.getCurrentPage();
    const prevPage = currentPage <= 0 ? this.dots.length - 1 : currentPage - 1;
    this.scrollToPage(prevPage);
  }

  snapToNearestPage() {
    this.scrollToPage(this.getCurrentPage());
  }

  /**
   * @param {number} index
   */
  scrollToPage(index) {
    if (!this.track) return;
    const cards = Array.from(this.track.children);
    const targetCard = cards[index];
    if (!(targetCard instanceof HTMLElement)) return;
    const cardLeft = targetCard.offsetLeft;
    const trackLeft = this.track.offsetLeft;

    this.track.scrollTo({
      left: cardLeft - trackLeft,
      behavior: 'smooth',
    });
  }

  getCurrentPage() {
    if (!this.track) return 0;
    const cards = Array.from(this.track.children);
    if (!cards.length) return 0;

    const scrollLeft = this.track.scrollLeft;
    let closestIndex = 0;
    let minDiff = Infinity;

    cards.forEach((card, i) => {
      if (card instanceof HTMLElement && this.track) {
        const diff = Math.abs(card.offsetLeft - this.track.offsetLeft - scrollLeft);
        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = i;
        }
      }
    });

    return Math.max(0, Math.min(closestIndex, this.dots.length - 1));
  }

  updatePagination() {
    if (this.dots.length === 0) return;
    const currentPage = this.getCurrentPage();

    this.dots.forEach((dot, index) => {
      const isActive = index === currentPage;
      dot.classList.toggle('custom-review-carousel__dot--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('custom-review-carousel')) {
  customElements.define('custom-review-carousel', CustomReviewCarousel);
}
